import { Dispatch, SetStateAction } from "react";
import { useRouter } from "next/router";
import React, { useEffect, useCallback } from "react";
import { useSettingsContext } from "~/providers/SettingsContextProvider";
import { sortTerms } from "~/utils/terms";
import { FilterCategory } from "../forms/FilterCategory";
import { FilterFieldset, FilterLegend } from "../styled/FilterFieldset";
import PageMargins from "../styled/PageMargins";
import { Reveal } from "../ui/Reveal";
import { FilterDateRange } from "../forms/FilterDateRange";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { SkipToLink } from "../ui/SkipToLink";

const DATE_RANGE_BEGIN_MONTH = 0; // remember JS month are 0 indexed
const DATE_RANGE_BEGIN_YEAR = 1990;
const DATE_RANGE_ADDITIONAL_YEARS = 1;
const TYPES = ["video", "audio_file", "podcast"];

export type FilterData = {
  mediaCategoryId: string[];
  type: string[];
  page: number;
  datePublishBegin: string;
  datePublishEnd: string;
};

export const defaultFilterData: FilterData = {
  type: TYPES,
  mediaCategoryId: [],
  page: 1,
  datePublishBegin: "",
  datePublishEnd: "",
};

export const FILTER_BASE_URL = "/media/filter";

export const MediaFilter = ({
  id,
  filterBase = FILTER_BASE_URL,
  showTypeFilter = true,
  types = TYPES,
  activeMediaCategoryId,
  isFilterOpen,
  initiallyOpen,
  filterData,
  setFilterData,
  filterQuery,
  setFilterQuery,
}: {
  id: string;
  types?: string[];
  filterBase?: string;
  filterData: FilterData;
  isFilterOpen: boolean;
  showTypeFilter?: boolean;
  initiallyOpen: boolean;
  filterQuery: string;
  activeMediaCategoryId?: number;
  setFilterData: Dispatch<SetStateAction<FilterData>>;
  setFilterQuery: Dispatch<SetStateAction<string>>;
}) => {
  const settings = useSettingsContext();
  const router = useRouter();
  const config = useConfigContext();

  const filterBaseUrl = `${config.baseUrl}${filterBase}`;

  const createQueryParams = (data: FilterData) => {
    const query = [];
    if (data?.mediaCategoryId?.length)
      query.push(`mediaCategoryId=${data.mediaCategoryId.join(",")}`);

    if (data?.datePublishBegin)
      query.push(
        `datePublishBegin=${data.datePublishBegin}&datePublishBeginCompare=gte`
      );

    if (data?.datePublishEnd)
      query.push(
        `datePublishEnd=${data.datePublishEnd}&datePublishEndCompare=lte`
      );

    if (data?.type?.length) query.push(`type=${data.type.join(",")}`);

    query.push("order=desc");
    query.push("orderby=post_date");
    query.push(`page=${data.page}`);

    return `?${query.join("&")}`;
  };

  const toggleMediaCategory = useCallback(
    (termId: any) => {
      const data = { ...filterData };

      if (Array.isArray(data.mediaCategoryId)) {
        if (data.mediaCategoryId.includes(`${termId}`)) {
          data.mediaCategoryId = data.mediaCategoryId.filter(
            (id: any) => `${id}` !== `${termId}`
          );
        } else {
          data.mediaCategoryId.push(`${termId}`);
        }
        if (activeMediaCategoryId && termId !== activeMediaCategoryId) {
          data.mediaCategoryId.push(`${activeMediaCategoryId}`);
        }
        data.page = 1;
        router.push(`${filterBaseUrl}${createQueryParams(data)}`, undefined, {
          shallow: true,
        });
      }
    },
    [filterBaseUrl, filterData, router, activeMediaCategoryId]
  );

  const toggleType = useCallback(
    (type: string) => {
      const data = { ...filterData };
      if (Array.isArray(data.type)) {
        if (data.type.includes(type)) {
          data.type = data.type.filter((t: any) => t !== type);

          if (!data.type.length) {
            data.type = TYPES.filter((t: any) => t !== type);
          }
        } else {
          data.type.push(type);
        }
        data.page = 1;
        router.push(`${filterBaseUrl}${createQueryParams(data)}`, undefined, {
          shallow: true,
        });
      }
    },
    [filterBaseUrl, filterData, router]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const setQueryParams = () => {
      const params = new URLSearchParams(document.location.search);

      const data: FilterData = { ...defaultFilterData };

      const mediaCategoryId = params.get("mediaCategoryId") ?? "";
      if (mediaCategoryId) {
        data.mediaCategoryId = mediaCategoryId.split(",") ?? [];
      } else {
        data.mediaCategoryId = [];
      }

      const type = params.get("type") ?? "";
      if (type) {
        data.type = type.split(",");
      } else {
        data.type = types;
      }

      const page = params.get("page") ?? "1";
      if (page) data.page = parseInt(page);

      if (params.get("datePublishBegin"))
        data.datePublishBegin = params.get("datePublishBegin") ?? "";

      if (params.get("datePublishEnd"))
        data.datePublishEnd = params.get("datePublishEnd") ?? "";

      setFilterData(data);
      setFilterQuery(createQueryParams(data));
    };

    if (filterQuery === "") setQueryParams();

    router.events.on("routeChangeComplete", setQueryParams);

    return () => {
      router.events.off("routeChangeComplete", setQueryParams);
    };
  }, [filterQuery, router.events, setFilterQuery, setFilterData, types]);

  return (
    <PageMargins>
      <Reveal
        id="media-filter"
        open={isFilterOpen}
        role="form"
        initiallyOpen={initiallyOpen}
      >
        <SkipToLink id="listing">skip filters</SkipToLink>
    
        <form action="/news/filter/" method="GET">
          {showTypeFilter && (
            <FilterFieldset addPaddingTop>
              <FilterLegend>TYPE</FilterLegend>
              {types.map((type: string, index: number) => {
                return (
                  <FilterCategory
                    defaultChecked={
                      Array.isArray(filterData.type) &&
                      filterData.type.includes(type)
                    }
                    name="type"
                    key={`filter-type-${index}`}
                    id={`filter-type-${index}`}
                    value={type}
                    label={type === "audio_file" ? "Audio" : type}
                    onClick={() => {
                      toggleType(type);
                    }}
                  />
                );
              })}
            </FilterFieldset>
          )}
          {settings?.taxonomies?.mediaCategory?.length > 0 && (
            <FilterFieldset addPaddingTop>
              <FilterLegend>CATEGORIES</FilterLegend>
              {sortTerms(settings?.taxonomies?.mediaCategory).map(
                (term: any, index: number) => {
                  return (
                    <FilterCategory
                      defaultChecked={
                        term.id === activeMediaCategoryId ||
                        (Array.isArray(filterData.mediaCategoryId) &&
                          filterData.mediaCategoryId.includes(`${term.id}`))
                      }
                      name="media_category"
                      key={`filter-cat-${term.id}`}
                      id={`filter-cat-${term.id}`}
                      value={term.id}
                      label={term.name}
                      onClick={() => {
                        toggleMediaCategory(term.id);
                      }}
                    />
                  );
                }
              )}
            </FilterFieldset>
          )}
          <FilterFieldset addPaddingTop>
            <FilterLegend>Published BETWEEN</FilterLegend>
            <FilterDateRange
              firstMonth={DATE_RANGE_BEGIN_MONTH}
              firstYear={DATE_RANGE_BEGIN_YEAR}
              addUntilYears={DATE_RANGE_ADDITIONAL_YEARS}
              from={filterData.datePublishBegin}
              until={filterData.datePublishEnd}
              onRangeChange={(from, until) => {
                const data = { ...filterData };
                if (from?.getTime() && until?.getTime()) {
                  data.datePublishBegin =
                    from.toISOString()?.split("T")?.[0] ?? "";
                  data.datePublishEnd =
                    new Date(until.getFullYear(), until.getMonth() + 1, 0)
                      .toISOString()
                      ?.split("T")?.[0] ?? "";
                } else {
                  data.datePublishBegin = "";
                  data.datePublishEnd = "";
                }

                data.page = 1;

                router.push(
                  `${filterBaseUrl}${createQueryParams(data)}`,
                  undefined,
                  {
                    shallow: true,
                  }
                );
              }}
            />
          </FilterFieldset>
        </form>
      </Reveal>
    </PageMargins>
  );
};
